import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { voidPurchaseRpc } from '@/services/purchases';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Purchase } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  Ban,
  AlertTriangle,
  Loader2,
  DollarSign,
  Package,
  Laptop as LaptopIcon,
} from 'lucide-react';

interface PurchaseCancelModalProps {
  purchase: Purchase;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const VOID_PRESETS = [
  'Received incorrect batch of goods from supplier',
  'Invoice cancelled / voided with supplier',
  'Entered duplicate purchase invoice',
  'Returned entire shipment to vendor',
  'Administrative error / Stock intake correction',
  'Other (specify below)',
];

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return 'Rs. 0';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const PurchaseCancelModal: React.FC<PurchaseCancelModalProps> = ({
  purchase,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState<string>(VOID_PRESETS[0]);
  const [customReason, setCustomReason] = useState<string>('');
  const [reversePayment, setReversePayment] = useState<boolean>(purchase.amount_paid > 0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const finalReason = customReason.trim()
    ? `${preset}: ${customReason.trim()}`
    : preset;

  const mutation = useMutation({
    mutationFn: async () => {
      if (!finalReason.trim()) {
        throw new Error('A reason is required to cancel this purchase.');
      }
      return await voidPurchaseRpc({
        purchaseId: purchase.id,
        reason: finalReason.trim(),
        reversePayment,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.purchases.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.purchases.detail(purchase.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      onClose();
      if (onSuccess) onSuccess();
    },
    onError: (err: unknown) => {
      setErrorMsg(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    mutation.mutate();
  };

  const laptopItemsCount = purchase.items?.filter((i) => i.item_type === 'laptop').length ?? 0;
  const accessoryItemsCount = purchase.items?.filter((i) => i.item_type === 'accessory').length ?? 0;

  return (
    <Modal
      isOpen={isOpen}
      title={`Cancel / Void Purchase — ${purchase.purchase_number}`}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorMsg && <ErrorAlert message={errorMsg} />}

        {/* Warning Banner */}
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2 text-xs text-red-900">
          <AlertTriangle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-bold">Irreversible Administrative Action</p>
            <p className="text-[11px] text-red-800 leading-relaxed">
              Cancelling this purchase will mark its status as <strong>cancelled</strong>, remove untouched laptops from active stock (marking them as scrapped), and reverse received accessory quantities.
            </p>
          </div>
        </div>

        {/* Purchase Summary */}
        <div className="p-3 bg-brand-bg rounded-lg border border-brand-border space-y-2 text-xs">
          <div className="flex items-center justify-between border-b border-brand-border pb-2">
            <div>
              <span className="text-brand-muted block text-[10px] uppercase font-bold">Purchase Number</span>
              <strong className="text-brand-black font-mono text-sm">{purchase.purchase_number}</strong>
            </div>
            <div className="text-right">
              <span className="text-brand-muted block text-[10px] uppercase font-bold">Supplier</span>
              <strong className="text-brand-black">{purchase.supplier?.name || 'N/A'}</strong>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1 text-[11px]">
            <div>
              <span className="text-brand-muted block">Total Cost</span>
              <span className="font-bold text-brand-black">{formatCurrency(purchase.total)}</span>
            </div>
            <div>
              <span className="text-brand-muted block">Amount Paid</span>
              <span className={`font-bold ${purchase.amount_paid > 0 ? 'text-amber-700' : 'text-brand-muted'}`}>
                {formatCurrency(purchase.amount_paid)}
              </span>
            </div>
            <div>
              <span className="text-brand-muted block">Items in Purchase</span>
              <span className="font-medium text-brand-black flex items-center space-x-2">
                {laptopItemsCount > 0 && (
                  <span className="inline-flex items-center space-x-0.5">
                    <LaptopIcon className="w-3 h-3 text-brand-green" />
                    <span>{laptopItemsCount} laptops</span>
                  </span>
                )}
                {accessoryItemsCount > 0 && (
                  <span className="inline-flex items-center space-x-0.5">
                    <Package className="w-3 h-3 text-blue-600" />
                    <span>{accessoryItemsCount} accessories</span>
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Payment Reversal Box (if amount_paid > 0) */}
        {purchase.amount_paid > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs space-y-2">
            <div className="flex items-start space-x-2 text-amber-900">
              <DollarSign className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">
                  Recorded Payments: {formatCurrency(purchase.amount_paid)}
                </p>
                <p className="text-[11px] text-amber-800 mt-0.5">
                  This purchase has payments recorded in accounting history.
                </p>
              </div>
            </div>

            <label className="flex items-center space-x-2 cursor-pointer pt-1 select-none">
              <input
                type="checkbox"
                checked={reversePayment}
                onChange={(e) => setReversePayment(e.target.checked)}
                className="w-4 h-4 text-brand-green rounded border-brand-border focus:ring-brand-green"
              />
              <span className="font-bold text-amber-950 text-xs">
                Record a compensating payment reversal ({formatCurrency(purchase.amount_paid)})
              </span>
            </label>
            <p className="text-[10px] text-amber-700 pl-6">
              Creates a balancing incoming payment record linked to this voided purchase to balance cash/bank accounts.
            </p>
          </div>
        )}

        {/* Reason Presets */}
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Cancellation Category <span className="text-brand-red">*</span>
          </label>
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs font-semibold text-brand-black focus:ring-2 focus:ring-brand-green"
          >
            {VOID_PRESETS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        {/* Custom Reason Details */}
        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Cancellation Details / Explanation <span className="text-brand-red">*</span>
          </label>
          <textarea
            rows={2}
            placeholder="Explain why this purchase is being voided..."
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs text-brand-black focus:ring-2 focus:ring-brand-green"
            required
          />
          <p className="text-[11px] text-brand-muted mt-1">
            This reason will be appended to the purchase record and logged in stock movements.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="px-4 py-2 text-xs font-medium text-brand-muted hover:text-brand-black transition-colors"
          >
            Keep Purchase
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || !finalReason.trim()}
            className="inline-flex items-center space-x-1.5 px-4 py-2 bg-brand-red text-white text-xs font-bold rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 shadow-sm"
          >
            {mutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Ban className="w-3.5 h-3.5" />
            )}
            <span>Confirm Void Purchase</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
